/**
 * CountersInput — operational counter form for the cleaner at submit time.
 *
 * PROJECT_STATE task #74a. Two number inputs, bounded 0–10, both
 * default 0. Renders as part of the TaskActionBar so the cleaner sees
 * them right above the submit button.
 *
 * Hard upper bound of 10 mirrors the schema CHECK on cleaning_tasks.
 * The per-tier soft limit + exceedance tripwire is #74b (depends on
 * #76 owner-approver / additional_fee_requests).
 */

import type { ChangeEvent, ReactNode } from 'react'
import { useTranslation }              from 'react-i18next'

const MIN = 0
const MAX = 10

interface Props {
  trashBagCount:                  number
  appliancesCleanedCount:         number
  onTrashBagCountChange:          (next: number) => void
  onAppliancesCleanedCountChange: (next: number) => void
  disabled?:                      boolean
}

export function CountersInput({
  trashBagCount,
  appliancesCleanedCount,
  onTrashBagCountChange,
  onAppliancesCleanedCountChange,
  disabled,
}: Props): ReactNode {
  const { t } = useTranslation()

  function clamp(raw: string): number | null {
    if (raw === '') return 0
    const parsed = Number.parseInt(raw, 10)
    if (Number.isNaN(parsed)) return null
    return Math.max(MIN, Math.min(MAX, parsed))
  }

  function handleChange(setter: (n: number) => void) {
    return (e: ChangeEvent<HTMLInputElement>): void => {
      const next = clamp(e.target.value)
      if (next !== null) setter(next)
    }
  }

  return (
    <fieldset
      data-testid="operational-counters"
      disabled={disabled}
      style={{
        display:      'flex',
        gap:          '0.75rem',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.6rem 0.8rem',
        margin:       0,
      }}
    >
      <legend
        style={{
          padding:  '0 0.3rem',
          fontSize: '0.75rem',
          color:    'var(--brand-neutral-500)',
        }}
      >
        {t('counters.legend', 'Recorded at submit')}
      </legend>
      <CounterField
        id="trash-bag-count"
        testId="trash-bag-count"
        label={t('counters.trashBags', 'Trash bags')}
        value={trashBagCount}
        onChange={handleChange(onTrashBagCountChange)}
      />
      <CounterField
        id="appliances-cleaned-count"
        testId="appliances-cleaned-count"
        label={t('counters.appliances', 'Appliances cleaned')}
        value={appliancesCleanedCount}
        onChange={handleChange(onAppliancesCleanedCountChange)}
      />
      <p
        data-testid="counters-bag-photo-hint"
        style={{
          flexBasis:  '100%',
          margin:     '0.25rem 0 0',
          fontSize:   '0.75rem',
          color:      'var(--brand-neutral-600)',
        }}
      >
        {t(
          'counters.bagPhotoHint',
          'Include one photo of all the bags from this room with your room photos — the count is part of payroll.',
        )}
      </p>
    </fieldset>
  )
}

interface CounterFieldProps {
  id:       string
  testId:   string
  label:    string
  value:    number
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
}

function CounterField({ id, testId, label, value, onChange }: CounterFieldProps): ReactNode {
  return (
    <label
      htmlFor={id}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '0.25rem' }}
    >
      <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>{label}</span>
      <input
        id={id}
        data-testid={testId}
        type="number"
        inputMode="numeric"
        min={MIN}
        max={MAX}
        step={1}
        value={value}
        onChange={onChange}
        style={{
          width:        '100%',
          padding:      '0.4rem 0.5rem',
          fontSize:     '0.9rem',
          borderRadius: 'var(--brand-radius-sm)',
          border:       '1px solid var(--brand-neutral-300)',
        }}
      />
    </label>
  )
}
